import type { ColumnDef } from "@tanstack/react-table"

import {
  ProductInventoryLotExpiresOnChip,
  ProductInventoryLotRowActions,
  ProductInventoryLotStatusChip,
} from "../components"

import type { InventoryByLotItem, Product } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"
import { formatWarehouseLabel } from "@/utils/warehouse"

interface UseProductInventoryLotColumnsProps {
  product: Product
  canMaintain: boolean
  onQualityStatusChange?: (inventory: InventoryByLotItem) => void
  onTransfer?: (inventory: InventoryByLotItem) => void
  onCoaView?: (inventory: InventoryByLotItem) => void
}

export const useProductInventoryLotColumns = ({
  product,
  canMaintain,
  onQualityStatusChange,
  onTransfer,
  onCoaView,
}: UseProductInventoryLotColumnsProps): ColumnDef<InventoryByLotItem>[] => {
  const uomName = product.packageUsWeightUomDetails?.name ?? ""

  return [
    {
      accessorKey: "lotNumber",
      header: "Lot #",
      cell: ({ row }) => row.original.lotNumber,
      enableSorting: false,
    },
    {
      accessorKey: "batchNumber",
      header: "Batch #",
      cell: ({ row }) => row.original.batchNumber,
      enableSorting: false,
    },
    {
      accessorKey: "packageSize",
      header: "Pkg UoM",
      cell: ({ row }) => (row.original.packageSize ? `${formatNumber(row.original.packageSize)} ${uomName}` : ""),
      enableSorting: false,
    },
    {
      accessorKey: "warehouse",
      header: "Warehouse",
      cell: ({ row }) => formatWarehouseLabel(row.original.warehouseArea!),
      enableSorting: true,
    },
    {
      accessorKey: "qtyOnHand",
      header: "Qty on Hand",
      cell: ({ row }) => `${formatNumber(row.original.qtyOnHand)} ${uomName}`,
      enableSorting: true,
    },
    {
      accessorKey: "qtyOnHold",
      header: "Qty on Hold",
      cell: ({ row }) => `${formatNumber(row.original.qtyOnHold)} ${uomName}`,
      enableSorting: true,
    },
    {
      accessorKey: "hardAllocatedQty",
      header: "Allocated",
      cell: ({ row }) => `${formatNumber(row.original.hardAllocatedQty)} ${uomName}`,
      enableSorting: true,
    },
    {
      accessorKey: "availableQty",
      header: "Available",
      cell: ({ row }) => `${formatNumber(row.original.availableQty)} ${uomName}`,
      enableSorting: true,
    },
    {
      accessorKey: "expireDate",
      header: "Expires",
      cell: ({ row }) => <ProductInventoryLotExpiresOnChip expiresOn={row.original.expireDate} />,
      enableSorting: true,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <ProductInventoryLotStatusChip status={row.original.status} />,
      enableSorting: true,
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }) => (
        <ProductInventoryLotRowActions
          row={row}
          canMaintain={canMaintain}
          onQualityStatusChange={onQualityStatusChange}
          onTransfer={onTransfer}
          onCoaView={onCoaView}
        />
      ),
      size: 50,
      enableSorting: false,
    },
  ]
}
